import { NotificationSetting } from 'types';

export const globalFetcherSettings = {
  keepPreviousData: true,
  // Must remain true: with revalidateIfStale also false, SWR skips the initial
  // fetch entirely when there is no cached value (hooks return {data:undefined,
  // isLoading:false} forever). The 30s dedupingInterval below already prevents
  // redundant refetches when components remount within a normal navigation
  // window.
  revalidateOnMount: true,
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  errorRetryCount: 1,
  // 5s was too short for common navigation patterns (Home → Payroll → Home),
  // forcing large payloads to be re-fetched on every return visit.
  dedupingInterval: 30000,
};

export const IncorporationStatusTextStyle = {
  PENDING: { text: 'In Progress', status: 'inProgress' },
  COMPLETED: { text: 'Completed', status: 'ready' },
};

export const invoiceStatusTextStyle = {
  OPEN: { text: 'Open', status: 'neutral' },
  SUCCEEDED: { text: 'Paid', status: 'ready' },
  PAID: { text: 'Paid', status: 'ready' },
  VOID: { text: 'Void', status: 'warning' },
  FAILED: { text: 'Failed', status: 'warning' },
  PROCESSING: { text: 'Processing', status: 'inProgress' },
  PAYMENT_FAILED_BUT_TRANSFER_SUCCEEDED: { text: 'Paid', status: 'ready' },
  PAYMENT_SUCCEEDED_BUT_TRANSFER_FAILED: { text: 'Failed', status: 'warning' },
  TRANSFER_SUCCEEDED: { text: 'Paid', status: 'ready' },
  TRANSFER_FAILED: { text: 'Failed', status: 'warning' },
  // OVERDUE, DUE, NOT_DUE are calculated
  OVERDUE: { text: 'Overdue', status: 'warning' },
  DUE: { text: 'Due', status: 'neutral' },
  NOT_DUE: { text: 'Not Due', status: 'neutral' },
  APPROVED: { text: 'Approved', status: 'ready' },
};

export const IRS1099StatusTextStyle = {
  FILED: { text: 'Filed', status: 'ready' },
  NOT_FILED: { text: 'Not Filed', status: 'inProgress' },
  PROCESSING: { text: 'Processing', status: 'inProgress' },
  READY_TO_FILE: { text: 'Ready to File', status: 'thera' },
  NOT_APPLICABLE: { text: 'Not Applicable', status: 'neutral' },
};

// Note: DueStatus should match the keys of invoiceStatusTextStyle
export const enum DueStatus {
  OVERDUE = 'OVERDUE',
  DUE = 'DUE',
  NOT_DUE = 'NOT_DUE',
}

export const workerTransactionTextStyle = {
  PENDING: {
    text: 'Processing',
    status: 'inProgress',
  },
  POSTED: {
    text: 'Completed',
    status: 'ready',
  },
  FAILED: {
    text: 'Failed',
    status: 'warning',
  },
};

export const withdrawalTransactionTextStyle = {
  ...workerTransactionTextStyle,
  POSTED: {
    text: 'Sent',
    status: 'ready',
  },
};

export const enum ContractType {
  FIXED_RATE = 'FIXED_RATE',
  FLEXIBLE_RATE = 'FLEXIBLE_RATE',
  MILESTONE = 'MILESTONE',
  TIME_TRACKING = 'TIME_TRACKING',
}

export const contractTypeLabel = {
  FLEXIBLE_RATE: 'PAY AS YOU GO',
  FIXED_RATE: 'FIXED RATE',
  MILESTONE: 'MILESTONE',
  TIME_TRACKING: 'TIME TRACKING',
};

export const enum ContractEstimateCalculationTypes {
  workingDaysCalculationType = 'workingDaysCalculationType',
  calendarDaysCalculationType = 'calendarDaysCalculationType',
}

export const ItemTypeInvoices = {
  EXPENSE: 'EXPENSE',
  COMMISSION: 'COMMISSION',
  BONUS: 'BONUS',
  OTHER: 'OTHER',
  OVERTIME: 'OVERTIME',
};

export const enum TheraAdvanceStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  ACTIVE = 'ACTIVE',
  REPAID = 'REPAID',
  CANCELLED = 'CANCELLED',
}

export const enum TheraAdvanceTransactionType {
  WITHDRAWAL = 'WITHDRAWAL',
  REPAYMENT = 'REPAYMENT',
}

export const enum TheraAdvanceTransactionStatus {
  COMPLETED = 'COMPLETED',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  FULLY_REPAID = 'FULLY_REPAID',
}

export const theraAdvanceTransactionTextStyle: Record<string, { text: string; status: string }> = {
  COMPLETED: { text: 'Completed', status: 'ready' },
  PARTIALLY_PAID: { text: 'Partial Paid', status: 'inProgress' },
  FULLY_REPAID: { text: 'Repaid', status: 'ready' },
};

export const getTheraAdvanceTransactionTextStyle = (status: string, isWithdrawal: boolean) =>
  isWithdrawal && status === TheraAdvanceTransactionStatus.COMPLETED
    ? { text: 'Sent', status: 'ready' }
    : theraAdvanceTransactionTextStyle[status];

export const enum InvoiceStatus {
  PROCESSING = 'PROCESSING',
  OPEN = 'OPEN',
  VOID = 'VOID',
  REQUIRES_ACTION = 'REQUIRES_ACTION',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  PAYMENT_FAILED_BUT_TRANSFER_SUCCEEDED = 'PAYMENT_FAILED_BUT_TRANSFER_SUCCEEDED', // success
  PAYMENT_SUCCEEDED_BUT_TRANSFER_FAILED = 'PAYMENT_SUCCEEDED_BUT_TRANSFER_FAILED', // failure
  TRANSFER_FAILED = 'TRANSFER_FAILED', // failure
  TRANSFER_SUCCEEDED = 'TRANSFER_SUCCEEDED', // success
}

export const enum InvoiceApprovalStatus {
  UNAPPROVED = 'UNAPPROVED',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  CANCELLED = 'CANCELLED',
}

export const enum StripeOnboardingStatus {
  STRIPE_LINK_COMPLETED = 'STRIPE_LINK_COMPLETED',
  STRIPE_ACCOUNT_MADE = 'STRIPE_ACCOUNT_MADE',
  INVITED = 'INVITED',
}

export const enum EmployeeType {
  EMPLOYEE = 'EMPLOYEE',
  US_EMPLOYEE = 'US_EMPLOYEE',
  CONTRACTOR = 'CONTRACTOR',
}

export const EmployeeTypeToSubRoleType = {
  EMPLOYEE: 'eorEmployee',
  US_EMPLOYEE: 'w2Employee',
  CONTRACTOR: 'contractor',
};

export const EmployeeTypeTextStyle = {
  EMPLOYEE: { text: 'Employee', status: 'neutral' },
  US_EMPLOYEE: { text: 'US Employee', status: 'neutral' },
  CONTRACTOR: { text: 'Contractor', status: 'neutral' },
};

export const payFrequencyDropdownValuesWithoutDaily = [
  { label: 'Weekly', id: 'weekly', value: 'weekly' },
  { label: 'Biweekly', id: 'biweekly', value: 'biweekly' },
  { label: 'Semimonthly', id: 'semimonthly', value: 'semimonthly' },
  { label: 'Monthly', id: 'monthly', value: 'monthly' },
  { label: 'Quarterly', id: 'quarterly', value: 'quarterly' },
  { label: 'Annually', id: 'annually', value: 'annually' },
];

export const payFrequencyDropdownValues = [
  { label: 'Daily', id: 'daily', value: 'daily' },
  ...payFrequencyDropdownValuesWithoutDaily,
];

export const enum OnboardingStatus {
  COMPLETE = 'COMPLETE',
  INCOMPLETE = 'INCOMPLETE',
}
export const onboardingStatusTextStyle = {
  COMPLETE: { text: 'Onboarded', status: 'ready' },
  INCOMPLETE: { text: 'Onboarding Incomplete', status: 'inProgress' },
  INACTIVE: { text: 'Inactive', status: 'neutral' },
};

export const US_States = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
  AS: 'American Samoa',
  GU: 'Guam',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  UM: 'U.S. Minor Outlying Islands',
  VI: 'U.S. Virgin Islands',
};

export const checkCompIntId = {
  // cspell:disable-next-line
  nextworkers: 'int_Fffk6nOGSHtuCTirWqlp', // use this exact ID for the Next integration
  // cspell:disable-next-line
  accrue: 'int_isqM1DgkMdj6DOPXOwgd', // use this exact ID for the Accrue integration
  // cspell:disable-next-line
  simplyinsured: 'int_Xf90bJAKE9Lf2jVjbgne', // use this exact ID for the SimplyInsured integration
  // cspell:disable-next-line
  humaninterest: 'int_21e08Fvrk5KS6cyzEekz', // use this exact ID for the Human Interest integration
};

export const nextWorkersCheckIntegrationId = 'int_Fffk6nOGSHtuCTirWqlp'; // cspell:disable-line
export const simplyInsuredId = 'int_Xf90bJAKE9Lf2jVjbgne'; // cspell:disable-line
export const accrueCompIntId = 'int_isqM1DgkMdj6DOPXOwgd'; // cspell:disable-line
export const humanInterestIntId = 'int_21e08Fvrk5KS6cyzEekz'; // cspell:disable-line
export const humanInterestPermissionId = 'ipe_54qwR1P7royqJkvTzHIu'; // cspell:disable-line

export const deductions = {
  '401k': {
    name: '401k',
    type: 'preTax',
    description:
      'A 401(k) plan is a tax-qualified, defined-contribution pension account that provides for pretax retirement savings.',
  },
  hsa: {
    name: 'HSA',
    type: 'preTax',
    description:
      'A health savings account (HSA) is a tax-advantaged medical savings account enrolled in a high-deductible health plan. Funds accumulate and can be used to pay for qualified medical expenses without tax liabilities.',
  },
  garnishment: {
    name: 'Garnishment',
    type: 'postTax',
    description:
      'A Wage Garnishment is a court-mandated withholding of an employee’s earnings from a paycheck for use toward debts. Common sources include: Child support, federal debt, state debt, student loans, and credit card debt. This serves as a post-tax deduction.',
  },
  miscellaneous: {
    name: 'Miscellaneous',
    type: 'postTax',
    description: 'Use this type for a generic post-tax deduction on the employee check.',
  },
  section_125: {
    name: 'Section 125',
    type: 'preTax',
    description:
      'A benefit belonging to a Section 125 plan that allows employees to choose between different types of qualified benefits.',
  },
  roth_401k: {
    name: 'Roth 401k',
    type: 'postTax',
    description:
      "The Roth 401(k) is a defined-contribution pension account that requires that the income tax be paid immediately, so the employee's real net income is reduced by the amount earmarked for savings. Employers can match employee contributions to a roth 401k, but those employer contributions must be matched in a separate traditional 401k deduction.",
  },
  '403b': {
    name: '403(b)',
    type: 'preTax',
    description:
      "A 403(b) plan (tax-sheltered annuity plan or TSA) is a retirement plan offered by public schools, non-profit organizations, and government entities. It's similar to a 401(k) plan maintained by a for-profit entity. Just as with a 401(k) plan, a 403(b) plan lets employees defer some of their salary into individual accounts.",
  },
  simple_ira: {
    name: 'Simple IRA',
    type: 'preTax',
    description:
      'A SIMPLE IRA plan (Savings Incentive Match PLan for Employees) allows employees and employers to contribute to traditional IRAs set up for employees by usually smaller businesses or companies.',
  },
  roth_ira: {
    name: 'Roth IRA',
    type: 'postTax',
    description:
      'A Individual Retirement Account to which you contribute after-tax dollars. Your contributions and earnings can grow tax-free, and you can withdraw them tax- and penalty-free after age 59½ and once the account has been open for five years.',
  },
};

export const earnings = {
  reimbursements: {
    name: 'Reimbursements',
    type: 'reimbursement',
    description:
      'A reimbursement is a payment made to an employee for expenses incurred on behalf of the company. This can include travel expenses, office supplies, or other expenses that the employee paid for out of pocket.',
  },
  'recurring-earnings': {
    name: 'Earnings',
    type: 'earning',
    description:
      "Recurring earnings like bonuses and commissions are added to the employee's paycheck every pay period automatically.",
  },
};

export const nonHourlyEarningTypes = [
  { value: 'bonus', label: 'Bonus' },
  { value: 'commission', label: 'Commission' },
  { value: 'shareholderHsa', label: '2% Shareholder HSA' },
  { value: 'shareholderBenefits', label: '2% Shareholder Benefits' },
];

export const rateMap = {
  HOURLY: ' / hour',
  DAILY: ' / day',
  WEEKLY: ' / week',
  BIWEEKLY: ' / 2 weeks',
  MONTHLY: ' / month',
  MILESTONE: ' / task',
  SEMIMONTHLY: ' / every 15 days',
};

export const employmentTypeLabel = {
  US_EMPLOYEE: 'US Employee',
  EMPLOYEE: 'EOR Employee',
  CONTRACTOR: 'Contractor',
};

export const contractTypeMap = {
  FIXED_RATE: 'fixed',
  FLEXIBLE_RATE: 'pay as you go',
  MILESTONE: 'milestone',
  TIME_TRACKING: 'time tracking',
};

export const wageTypeToWorkUnitType = {
  HOURLY: 'hour',
  DAILY: 'day',
  WEEKLY: 'week',
  MONTHLY: 'month',
  MILESTONE: 'task',
};

export const wageTypeMapping = {
  HOURLY: 'Hourly',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Bi-Weekly',
  MONTHLY: 'Monthly',
  SEMIMONTHLY: 'Semi Monthly',
  MILESTONE: 'Milestone',
};

export const employeeStatusLabel = {
  ACTIVE: 'Active' as const,
  INACTIVE: 'Inactive' as const,
};

export enum KycDocumentType {
  ID_CARD = 'ID_CARD',
  PASSPORT = 'PASSPORT',
  DRIVER_LICENSE = 'DRIVER_LICENSE',
  RESIDENT_CARD = 'RESIDENT_CARD',
  UNKNOWN = 'UNKNOWN',
}

export enum AiPriseVerificationStatus {
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  REVIEW = 'REVIEW',
  UNKNOWN = 'UNKNOWN',
}

export enum WithdrawalAccountType {
  BANK_TRANSFER = 'BANK_TRANSFER',
  WISE = 'WISE',
  PAYONEER = 'PAYONEER',
  DOLARAPP = 'DOLARAPP',
  TAKENOS = 'TAKENOS',
  ASTROPAY = 'ASTROPAY',
  PAYPAL = 'PAYPAL',
  THERA_WALLET = 'THERA_WALLET',
  // cspell:ignore DLOCAL
  DLOCAL_STABLECOIN = 'DLOCAL_STABLECOIN',
}

// Ledger transaction IDs to hide from the UI
export const HIDDEN_LEDGER_TRANSACTION_IDS = [
  // man.srz@gmail.com
  '019b5c0d-ffae-7817-9b7b-8760d37d3070',
  '019b5c0d-a68b-7e33-af02-1808f62bd868',
  '019b4d17-2d6b-7d59-8b7a-ade089450715',
];

export enum TheraTeamRoles {
  role_admin = 'role_admin',
  role_org_manager = 'role_org_manager',
  role_people_manager = 'role_people_manager',
  role_people_manager_and_payer = 'role_people_manager_and_payer',
  role_viewer_sensitive = 'role_viewer_sensitive',
}

export enum TheraOrgRoles {
  role_admin = 'role_admin',
  role_manager = 'role_manager',
  role_apar_manager = 'role_apar_manager',
  role_org_manager = 'role_org_manager',
}

export const TheraTeamRolesDesc = {
  [TheraTeamRoles.role_admin]: {
    name: 'Team Admin',
    description: 'Can create, edit, and manage contracts, pay invoices and manage team settings.',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  [TheraTeamRoles.role_org_manager]: {
    name: 'HR Specialist',
    description: "Can create, edit, and manage contracts and invoices but can't pay invoices.",
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  [TheraTeamRoles.role_people_manager]: {
    name: 'People Manager',
    description: 'Can create, edit, and manage contracts.',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  [TheraTeamRoles.role_people_manager_and_payer]: {
    name: 'People Manager and Payer',
    description: 'Can create, edit, and manage contracts, and pay invoices.',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  [TheraTeamRoles.role_viewer_sensitive]: {
    name: 'Viewer',
    description: 'Can view contractors and information.',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
};

export const TheraOrgRolesDesc = {
  [TheraOrgRoles.role_admin]: {
    name: 'Admin',
    description: '',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  [TheraOrgRoles.role_manager]: {
    name: 'Manager',
    description: '',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  [TheraOrgRoles.role_org_manager]: {
    name: 'HR Specialist',
    description: '',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
};

export const TheraManagerExtraPermissions = {
  perm_adjust_payroll_and_payments: {
    id: 'perm_adjust_payroll_and_payments',
    name: 'Can adjust invoices',
    description: 'Can open invoices.',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
  perm_approve_payroll_and_payments: {
    id: 'perm_approve_payroll_and_payments',
    name: 'Can approve time off requests and invoices',
    description: 'Can approve or deny time off requests and invoices before payment.',
    learnMoreLink: 'https://intercom.help/thera/en/articles/8464059-what-are-the-different-roles-for-managers-in-thera',
  },
};

export const THERA_NAME = 'Bloom Financial Corp.';
export const THERA_ADDRESS = '1460 Broadway, New York, NY 10036, UNITED STATES';
export const theraBlue = '#2571CF';
/** @deprecated */
export const TheraCalendlyLink = 'https://calendly.com/d/3jb-5b3-qnn/meet-with-thera';
export const TheraBookAMeetingLink = 'https://www.getthera.com/book-a-demo?utm_source=thera-app';
// Default.com demo scheduler — generic public booking link for the queue (no
// per-session id). Used in the native app to book a demo in-app, bypassing the
// getthera.com lead form + its ad-cookie banner (App Store 5.1.2(i)). Unlike the
// website's `?source=workflow` URL, this one prompts each attendee for their own
// name/email, so every user gets their own confirmation email.
export const TheraDemoSchedulerLink = 'https://scheduler.default.com/13482/queue/6681';

export const complianceDocumentLabels = {
  PASSPORT_OR_ID: 'Passport or National ID',
  TIN: 'Tax Identification Number (TIN)',
  GST_REGISTRATION_NUMBER_OR_TAN: 'GST Registration Document',
  ENTITY_PROOF_OF_REGISTRATION: 'Entity Registration Document',
  ANNUAL_RETURNS_PAYMENT: 'Proof of Annual Returns Payment',
  REGISTRATION_UNDER_INCOME_TAX_LAWS: 'Proof of Registration under Income Tax Laws',
  PIN_CERTIFICATE: 'Personal Identification Number Certificate',
  NHIF_MEMBERSHIP: 'Proof of Membership with the National Hospital Insurance Fund',
  SECTOR_SPECIFIC_LICENSES_OR_PERMITS: 'Relevant Sector-Specific Licenses and/or Permits',
  TAX_ID: 'Proof of Tax ID, including Tax ID Number',
  BANK_ACCOUNT: 'Proof of Bank Account',
  CURRENT_TAX_COMPLIANCE_CERTIFICATE: 'Current Tax Compliance Certificate',
  SOCIAL_SECURITY_CONTRIBUTIONS: 'Proof of Social Security Contributions',
  CPF: 'Cadastro de Pessoas Físicas (CPF)',
  PIS_NISS: 'Número de Inscrição na Segurança Social (PIS/NISS)',
  RNC: 'Proof of RNC',
  AUTHORIZED_NCF_NUMBER: 'Proof of Authorized NCF Number',
  HST_REGISTRATION: 'Proof of HST Registration',
  ARTICLES_OF_INCORPORATION: 'Articles of Incorporation and By-Laws',
  CERTIFICATE_OF_GOOD_STANDING: 'Certificate of Good Standing',
  FEDERAL_TAXPAYER_REGISTRY: 'Proof of Federal Taxpayer Registry Number',
  EMPLOYER_REGISTRATION: 'Proof of Employer Registration Number',
  CTC: 'Community Tax Certificate',
};

export const TheraUSBankDetails = {
  'SWIFT/BIC Code': 'CHFGUS44021', // cspell:disable-line
  'ABA Routing Number': '091311229',
  'Bank Name': 'Choice Financial Group',
  'Bank Address': '4501 23rd Avenue S Fargo, ND 58104',
  'Beneficiary Name': 'Bloom Financial Corp.',
  'Beneficiary Account Number': '202326846519',
  'Beneficiary Address': '1460 Broadway, New York, NY 10036',
};

// ACH originator details to share with a company's bank, keyed by AchProvider plus DWOLLA, which the backend
// files under STRIPE (see getAchDebitOriginator).
export const AchProviderDetails: Record<
  'TP' | 'STRIPE' | 'CHECK' | 'DWOLLA',
  { label: string; value: string; showCopyButton?: boolean; note?: string }[]
> = {
  TP: [
    { label: 'Company name', value: 'Thera', showCopyButton: false },
    { label: 'Company ID', value: 'BFDBATHERA' }, // cspell:disable-line
  ],
  STRIPE: [
    { label: 'Organization', value: 'Stripe Payments Company', showCopyButton: false },
    { label: 'Company ID (ACH ID)', value: '1800948598' },
    { label: 'Company ID (ACH ID)', value: '4270465600' },
  ],
  CHECK: [
    { label: 'Organization', value: 'Check Technologies Inc', showCopyButton: false },
    { label: 'Company ID (ACH ID)', value: '1209306000' },
  ],
  // The trailing space is part of Dwolla's 10-character company ID.
  DWOLLA: [
    { label: 'Organization', value: 'Dwolla, Inc.', showCopyButton: false },
    {
      label: 'Company ID (ACH ID)',
      value: '945440567 ',
      note: 'The ID is 10 characters and ends with a space.',
    },
  ],
};

export type AchOriginator = keyof typeof AchProviderDetails;

export const CompanyLegalStructure = {
  ccorp: 'C Corp',
  corp: 'Corporation',
  estate: 'Estate',
  foreign_entity: 'Foreign Entity',
  llc: 'LLC',
  llp: 'LLP',
  lp: 'LP',
  nonprofit: 'Nonprofit',
  partnership: 'Partnership',
  scheme: 'Scheme',
  scorp: 'S Corp',
  soleprop: 'Sole Proprietorship',
  trust: 'Trust',
};

export const TheraGBBankDetails = {
  'Account Holder': 'Bloom Financial Corp.',
  'Account number': '27118228',
  'Sort Code': '23-14-70',
  IBAN: 'GB20 TRWI 2314 7027 1182 28', // cspell:disable-line
  'Bank Name': 'Wise Payments Limited',
  'Bank Address': '56 Shoreditch High Street, London, E1 6JJ, United Kingdom',
};

export const TheraAUDBankDetails = {
  'Account Holder': 'Bloom Financial Corp.',
  'Account number': '211830105',
  'BSB code': {
    value: '774001',
    note: 'Use when sending money from Australia',
  },
  'Swift/BIC': {
    value: 'TRWIAUS1XXX',
    note: 'Use when sending money from outside Australia',
  },
  'Bank Name': 'Wise Australia Pty Ltd',
  'Bank Address': 'Suite 1, Level 11, 66 Goulburn Street, Sydney, NSW, 2000, Australia',
};

export const CHECK_EARNING_RATE_NAME = 'regular_compensation';
export const CHECK_OVERTIME_EARNING_RATE_NAME = 'overtime_rate';
export const CHECK_DOUBLE_OVERTIME_EARNING_RATE_NAME = 'double_overtime_rate';

export const checkEmployeePaystubStatusTextStyle = {
  paid: { text: 'Paid', status: 'ready' },
  draft: { text: 'Draft', status: 'inProgress' },
  pending: { text: 'Pending', status: 'inProgress' },
  processing: { text: 'Processing', status: 'inProgress' },
  partially_paid: { text: 'Partially Paid', status: 'warning' },
  failed: { text: 'Failed', status: 'warning' },
  void: { text: 'Void', status: 'neutral' },
};

export const employeeCheckApprovalStatusTextStyle = {
  APPROVED: { text: 'Approved', status: 'ready' },
  ACTION_NEEDED: { text: 'Action Needed', status: 'inProgress' },
};

export const CheckIndustryType = {
  auto_or_machine_sales: 'Auto or Machine Sales',
  auto_or_machine_repair: 'Auto or Machine Repair',
  arts_or_entertainment_or_recreation: 'Arts or Entertainment or Recreation',
  cleaning_services: 'Cleaning Services',
  consulting_services: 'Consulting Services',
  educational_services: 'Educational Services',
  family_care_services: 'Family Care Services',
  financial_services: 'Financial Services',
  food_and_beverage_retail_or_wholesale: 'Food and Beverage Retail or Wholesale',
  general_construction_or_general_contracting: 'General Construction or General Contracting',
  health_care: 'Health Care',
  hospitality_or_accommodation: 'Hospitality or Accommodation',
  hvac_or_plumbing_or_electrical_contracting: 'HVAC or Plumbing or Electrical Contracting',
  legal_services: 'Legal Services',
  non_food_retail_or_wholesale: 'Non-food Retail or Wholesale',
  other: 'Other',
  personal_care_services: 'Personal Care Services',
  real_estate: 'Real Estate',
  restaurant: 'Restaurant',
  scientific_or_technical_services: 'Scientific or Technical Services',
  security_services: 'Security Services',
  staffing: 'Staffing',
  tobacco_or_alcohol_sales: 'Tobacco or Alcohol Sales',
  transportation: 'Transportation',
};

export const CheckBusinessType = {
  sole_proprietorship: 'Sole Proprietorship',
  partnership: 'Partnership',
  c_corporation: 'C Corporation',
  s_corporation: 'S Corporation',
  llc: 'LLC', // Limited Liability Company
};
export const entityTypes = [
  { id: 'FOREIGN_COMPANY_BRANCH', value: 'FOREIGN_COMPANY_BRANCH', label: 'Foreign Company Branch' },
  { id: 'COMPANY_LIMITED_BY_GUARANTEE', value: 'COMPANY_LIMITED_BY_GUARANTEE', label: 'Company Limited by Guarantee' },
  { id: 'INCORPORATION', value: 'INCORPORATION', label: 'Incorporation' },
  { id: 'JOINT_STOCK_COMPANIES', value: 'JOINT_STOCK_COMPANIES', label: 'Joint Stock Companies' },
  {
    id: 'LIMITED_LIABILITY_PARTNERSHIP',
    value: 'LIMITED_LIABILITY_PARTNERSHIP',
    label: 'Limited Liability Partnership',
  },
  { id: 'NON_PROFIT_CORPORATION', value: 'NON_PROFIT_CORPORATION', label: 'Non-Profit Corporation' },
  {
    id: 'PRIVATE_COMPANY_LIMITED_BY_SHARES',
    value: 'PRIVATE_COMPANY_LIMITED_BY_SHARES',
    label: 'Private Company Limited by Shares',
  },
  { id: 'PRIVATE_LIABILITY_COMPANY', value: 'PRIVATE_LIABILITY_COMPANY', label: 'Private Liability Company' },
  {
    id: 'PUBLIC_COMPANY_LIMITED_BY_SHARES',
    value: 'PUBLIC_COMPANY_LIMITED_BY_SHARES',
    label: 'Public Company Limited by Shares',
  },
  { id: 'PUBLIC_LIABILITY_COMPANY', value: 'PUBLIC_LIABILITY_COMPANY', label: 'Public Liability Company' },
  {
    id: 'SOLE_PROPRIETOR_OR_SOLE_TRADER',
    value: 'SOLE_PROPRIETOR_OR_SOLE_TRADER',
    label: 'Sole Proprietor or Sole Trader',
  },
  { id: 'TRUSTS', value: 'TRUSTS', label: 'Trusts' },
  { id: 'UNLIMITED_COMPANY', value: 'UNLIMITED_COMPANY', label: 'Unlimited Company' },
  { id: 'REGISTERED_INDIVIDUAL', value: 'REGISTERED_INDIVIDUAL', label: 'Registered Individual' },
  { id: 'UNREGISTERED_INDIVIDUAL', value: 'UNREGISTERED_INDIVIDUAL', label: 'Unregistered Individual' },
  { id: 'SOLE_TRADER', value: 'SOLE_TRADER', label: 'Sole Trader' },
  { id: 'B_CORPORATION', value: 'B_CORPORATION', label: 'B Corporation' },
  { id: 'C_CORPORATION', value: 'C_CORPORATION', label: 'C Corporation' },
  { id: 'GENERAL_PARTNERSHIP', value: 'GENERAL_PARTNERSHIP', label: 'General Partnership' },
  { id: 'LIMITED_LIABILITY_COMPANY', value: 'LIMITED_LIABILITY_COMPANY', label: 'Limited Liability Company' },
  { id: 'LIMITED_PARTNERSHIP', value: 'LIMITED_PARTNERSHIP', label: 'Limited Partnership' },
  { id: 'NON_PROFIT_501C_3', value: 'NON_PROFIT_501C_3', label: 'Non-Profit 501(c)(3)' },
  { id: 'NON_PROFIT_OTHER', value: 'NON_PROFIT_OTHER', label: 'Non-Profit Other' },
  { id: 'S_CORPORATION', value: 'S_CORPORATION', label: 'S Corporation' },
  { id: 'SOLE_PROPRIETORSHIP', value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
  { id: 'OTHER', value: 'OTHER', label: 'Other' },
];

export const entityW8BENETypes = [
  { id: 'SIMPLE_TRUST', value: 'SIMPLE_TRUST', label: 'Simple Trust' },
  { id: 'CENTRAL_BANK_OF_ISSUE', value: 'CENTRAL_BANK_OF_ISSUE', label: 'Central Bank of Issue' },
  { id: 'GRANTOR_TRUST', value: 'GRANTOR_TRUST', label: 'Grantor Trust' },
  { id: 'TAX_EMPTY_ORG', value: 'TAX_EMPTY_ORG', label: 'Tax Empty Org' },
  { id: 'PRIVATE_FOUNDATION', value: 'PRIVATE_FOUNDATION', label: 'Private Foundation' },
  { id: 'DISREGARDED_ENTITY', value: 'DISREGARDED_ENTITY', label: 'Disregarded Entity' },
  { id: 'CORPORATION', value: 'CORPORATION', label: 'Corporation' },
  { id: 'COMPLEX_TRUST', value: 'COMPLEX_TRUST', label: 'Complex Trust' },
  { id: 'ESTATE', value: 'ESTATE', label: 'Estate' },
  { id: 'INTERNATIONAL_ORG', value: 'INTERNATIONAL_ORG', label: 'International Org' },
  { id: 'PARTNERSHIP', value: 'PARTNERSHIP', label: 'Partnership' },
  {
    id: 'FOREIGN_GOVERNMENT_CONTROLLED_ENTITY',
    value: 'FOREIGN_GOVERNMENT_CONTROLLED_ENTITY',
    label: 'Foreign Government-Controlled Entity',
  },
  {
    id: 'FOREIGN_GOVERNMENT_INTEGRAL_PART',
    value: 'FOREIGN_GOVERNMENT_INTEGRAL_PART',
    label: 'Foreign Government Integral Part',
  },
];

export const w9EntityTypes = [
  { id: 'INDIVIDUAL', value: 'INDIVIDUAL', label: 'Individual / Sole proprietor' },
  { id: 'C_CORPORATION', value: 'C_CORPORATION', label: 'C Corporation' },
  { id: 'S_CORPORATION', value: 'S_CORPORATION', label: 'S Corporation' },
  { id: 'PARTNERSHIP', value: 'PARTNERSHIP', label: 'Partnership' },
  { id: 'TRUST_OR_ESTATE', value: 'TRUST_OR_ESTATE', label: 'Trust or Estate' },
  {
    id: 'LIMITED_LIABILITY_COMPANY_TYPE_C',
    value: 'LIMITED_LIABILITY_COMPANY_TYPE_C',
    label: 'Limited Liability Company (Type C)',
  },
  {
    id: 'LIMITED_LIABILITY_COMPANY_TYPE_S',
    value: 'LIMITED_LIABILITY_COMPANY_TYPE_S',
    label: 'Limited Liability Company (Type S)',
  },
  {
    id: 'LIMITED_LIABILITY_COMPANY_TYPE_P',
    value: 'LIMITED_LIABILITY_COMPANY_TYPE_P',
    label: 'Limited Liability Company (Partnership)',
  },
  { id: 'OTHER', value: 'OTHER', label: 'Other' },
];

export const VatIdTypeValues = [
  { id: 'unknown', value: 'unknown', label: 'Unknown' },
  { id: 'au_abn', value: 'au_abn', label: 'Australian Business Number (Australia)', country: 'AU' },
  { id: 'au_arn', value: 'au_arn', label: 'Australian Taxation Office Reference Number (Australia)', country: 'AU' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Austria)', country: 'AT' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Belgium)', country: 'BE' },
  { id: 'br_cnpj', value: 'br_cnpj', label: 'Brazilian CNPJ number (Brazil)', country: 'BR' }, // cspell:disable-line
  { id: 'br_cpf', value: 'br_cpf', label: 'Brazilian CPF number (Brazil)', country: 'BR' },
  { id: 'bg_uic', value: 'bg_uic', label: 'Bulgaria Unified Identification Code (Bulgaria)', country: 'BG' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Bulgaria)', country: 'BG' },
  { id: 'ca_bn', value: 'ca_bn', label: 'Canadian BN (Canada)', country: 'CA' },
  { id: 'ca_gst_hst', value: 'ca_gst_hst', label: 'Canadian GST/HST number (Canada)', country: 'CA' },
  { id: 'ca_pst_bc', value: 'ca_pst_bc', label: 'Canadian PST number (British Columbia) (Canada)', country: 'CA' },
  { id: 'ca_pst_mb', value: 'ca_pst_mb', label: 'Canadian PST number (Manitoba) (Canada)', country: 'CA' },
  { id: 'ca_pst_sk', value: 'ca_pst_sk', label: 'Canadian PST number (Saskatchewan) (Canada)', country: 'CA' },
  { id: 'ca_qst', value: 'ca_qst', label: 'Canadian QST number (Québec) (Canada)', country: 'CA' },
  { id: 'cl_tin', value: 'cl_tin', label: 'Chilean TIN (Chile)', country: 'CL' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Croatia)', country: 'HR' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Cyprus)', country: 'CY' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Czech Republic)', country: 'CZ' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Denmark)', country: 'DK' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Estonia)', country: 'EE' },
  {
    id: 'eu_oss_vat',
    value: 'eu_oss_vat',
    label: 'European One Stop Shop VAT number for non-Union scheme (European Union)',
    country: 'EU',
  },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Finland)', country: 'FI' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (France)', country: 'FR' },
  { id: 'ge_vat', value: 'ge_vat', label: 'Georgian VAT (Georgia)', country: 'GE' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Germany)', country: 'DE' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Greece)', country: 'EL' },
  { id: 'hk_br', value: 'hk_br', label: 'Hong Kong BR number (Hong Kong)', country: 'HK' },
  { id: 'hu_tin', value: 'hu_tin', label: 'Hungary tax number (adószám) (Hungary)', country: 'HU' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Hungary)', country: 'HU' },
  { id: 'is_vat', value: 'is_vat', label: 'Icelandic VAT (Iceland)', country: 'IS' },
  { id: 'in_gst', value: 'in_gst', label: 'Indian GST number (India)', country: 'IN' },
  { id: 'id_npwp', value: 'id_npwp', label: 'Indonesian NPWP number (Indonesia)', country: 'ID' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Ireland)', country: 'IE' },
  { id: 'il_vat', value: 'il_vat', label: 'Israel VAT (Israel)', country: 'IL' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Italy)', country: 'IT' },
  { id: 'jp_cn', value: 'jp_cn', label: 'Japanese Corporate Number (Hōjin Bangō) (Japan)', country: 'JP' },
  {
    id: 'jp_rn',
    value: 'jp_rn',
    label:
      "Japanese Registered Foreign Businesses' Registration Number (Tōroku Kokugai Jigyōsha no Tōroku Bangō) (Japan)",
    country: 'JP',
  },
  { id: 'jp_trn', value: 'jp_trn', label: 'Japanese Tax Registration Number (Tōroku Bangō) (Japan)', country: 'JP' },
  {
    id: 'ke_pin',
    value: 'ke_pin',
    label: 'Kenya Revenue Authority Personal Identification Number (Kenya)',
    country: 'KE',
  },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Latvia)', country: 'LV' },
  { id: 'li_uid', value: 'li_uid', label: 'Liechtensteinian UID number (Liechtenstein)', country: 'LI' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Lithuania)', country: 'LT' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Luxembourg)', country: 'LU' },
  { id: 'my_frp', value: 'my_frp', label: 'Malaysian FRP number (Malaysia)', country: 'MY' },
  { id: 'my_itn', value: 'my_itn', label: 'Malaysian ITN (Malaysia)', country: 'MY' },
  { id: 'my_sst', value: 'my_sst', label: 'Malaysian SST number (Malaysia)', country: 'MY' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Malta)', country: 'MT' },
  { id: 'mx_rfc', value: 'mx_rfc', label: 'Mexican RFC number (Mexico)', country: 'MX' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Netherlands)', country: 'NL' },
  { id: 'nz_gst', value: 'nz_gst', label: 'New Zealand GST number (New Zealand)', country: 'NZ' },
  { id: 'no_vat', value: 'no_vat', label: 'Norwegian VAT number (Norway)', country: 'NO' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Poland)', country: 'PL' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Portugal)', country: 'PT' },
  { id: 'ru_inn', value: 'ru_inn', label: 'Russian INN (Russia)', country: 'RU' },
  { id: 'ru_kpp', value: 'ru_kpp', label: 'Russian KPP (Russia)', country: 'RU' },
  { id: 'sa_vat', value: 'sa_vat', label: 'Saudi Arabia VAT (Saudi Arabia)', country: 'SA' },
  { id: 'sg_gst', value: 'sg_gst', label: 'Singaporean GST (Singapore)', country: 'SG' },
  { id: 'sg_uen', value: 'sg_uen', label: 'Singaporean UEN (Singapore)', country: 'SG' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Slovakia)', country: 'SK' },
  { id: 'si_tin', value: 'si_tin', label: 'Slovenia tax number (davčna številka)', country: 'SI' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Slovenia)', country: 'SI' },
  { id: 'za_vat', value: 'za_vat', label: 'South African VAT number (South Africa)', country: 'ZA' },
  { id: 'kr_brn', value: 'kr_brn', label: 'Korean BRN (South Korea)', country: 'KR' },
  { id: 'es_cif', value: 'es_cif', label: 'Spanish NIF number (previously Spanish CIF number) (Spain)', country: 'ES' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Spain)', country: 'ES' },
  { id: 'eu_vat', value: 'eu_vat', label: 'European VAT number (Sweden)', country: 'SE' },
  { id: 'ch_vat', value: 'ch_vat', label: 'Switzerland VAT number (Switzerland)', country: 'CH' },
  { id: 'tw_vat', value: 'tw_vat', label: 'Taiwanese VAT (Taiwan)', country: 'TW' },
  { id: 'th_vat', value: 'th_vat', label: 'Thai VAT (Thailand)', country: 'TH' },
  { id: 'ua_vat', value: 'ua_vat', label: 'Ukrainian VAT (Ukraine)', country: 'UA' },
  { id: 'ae_trn', value: 'ae_trn', label: 'United Arab Emirates TRN (United Arab Emirates)', country: 'AE' },
  { id: 'gb_vat', value: 'gb_vat', label: 'United Kingdom VAT number (United Kingdom)', country: 'GB' },
  { id: 'eu_vat', value: 'eu_vat', label: 'Northern Ireland VAT number (United Kingdom)', country: 'GB' },
  {
    id: 'us_ein',
    value: 'us_ein',
    label: 'Federal Tax Identification Number or United States EIN (United States)',
    country: 'US',
  },
];

export const frequencyDropdownOptions = [
  {
    label: 'One-time',
    value: 'nonrecurring',
    id: 'nonrecurring',
  },
  { label: 'Recurring', value: 'recurring', id: 'recurring' },
];
export const startDayDropdownOptions = [
  {
    label: 'First of the month',
    value: 'first_day',
    id: 'first_day',
  },
  { label: 'End of the month', value: 'last_day', id: 'last_day' },
];

export const noVatRateList = ['US', 'PK'];

export const ptoStatusTextStyle = {
  USED: { text: 'Used', status: 'ready' },
  IN_REVIEW: { text: 'Pending Review', status: 'inProgress' },
  APPROVED: { text: 'Approved', status: 'ready' },
  DENIED: { text: 'Denied', status: 'warning' },
  CANCELLED: { text: 'Cancelled', status: 'neutral' },
};

export const timeOffPolicyTypeValues = [
  { id: 'Paid Leave', value: 'Paid Leave', label: 'Paid Leave' },
  { id: 'Unpaid Leave', value: 'Unpaid Leave', label: 'Unpaid Leave' },
  { id: 'Sick Leave', value: 'Sick Leave', label: 'Sick Leave' },
  { id: 'Vacation', value: 'Vacation', label: 'Vacation' },
  { id: 'Parental', value: 'Parental', label: 'Parental' },
];

export const timeOffPolicyPayTypeValues = [
  { id: 'PAID', value: 'PAID', label: 'Paid' },
  { id: 'UNPAID', value: 'UNPAID', label: 'Unpaid' },
];

export const timeOffPolicyMidyearJoiningValues = [
  { id: 'FULL_AMOUNT', value: 'FULL_AMOUNT', label: 'Full Amount' },
  { id: 'PRORATED_AMOUNT', value: 'PRORATED_AMOUNT', label: 'Prorated Amount' },
];

export const tpTransactionStatusTextStyle = {
  hold: { text: 'Hold', status: 'thera' },
  withdrawal: { text: 'Complete', status: 'ready' },
  deposit: { text: 'Complete', status: 'ready' },
  hold_release: { text: 'Complete', status: 'ready' },
  pending_deposit: { text: 'Pending', status: 'inProgress' },
  interest: { text: 'Interest', status: 'thera' },
  payment: { text: 'Payment', status: 'thera' },
  reversal: { text: 'Reversal', status: 'thera' },
  charge: { text: 'Charge', status: 'thera' },
};

// Constants for supported countries
export const DOLARAPP_SUPPORTED_COUNTRIES = [
  'MX', // Mexico
  'AR', // Argentina
  'BR', // Brazil
];

export const TAKENOS_SUPPORTED_COUNTRIES = [
  'AR', // Argentina
  'BO', // Bolivia
  'CL', // Chile
  'CO', // Colombia
  'CR', // Costa Rica
  'DO', // Dominican Republic
  'EC', // Ecuador
  'SV', // El Salvador
  'GT', // Guatemala
  'HN', // Honduras
  'MX', // Mexico
  'NI', // Nicaragua
  'PA', // Panama
  'PY', // Paraguay
  'PE', // Peru
  'UY', // Uruguay
  'US', // America
];

export const ASTROPAY_SUPPORTED_COUNTRIES = [
  // Americas
  'AR', // Argentina
  'BO', // Bolivia
  'BR', // Brazil
  'CA', // Canada
  'CL', // Chile
  'CO', // Colombia
  'EC', // Ecuador
  'PY', // Paraguay
  'PE', // Peru
  'MX', // Mexico
  'UY', // Uruguay

  // Europe
  'AT', // Austria
  'HR', // Croatia
  'FR', // France
  'DE', // Germany
  'GB', // United Kingdom
  'IM', // Isle of Man
  'IT', // Italy
  'NL', // Netherlands
  'NO', // Norway
  'PL', // Poland
  'PT', // Portugal
  'ES', // Spain
  'SE', // Sweden

  // Asia Pacific & Africa
  'AU', // Australia
  'BD', // Bangladesh
  'CM', // Cameroon
  'CN', // China
  'CI', // Côte d'Ivoire
  'EG', // Egypt
  'GH', // Ghana
  'IN', // India
  'KE', // Kenya
  'MY', // Malaysia
  'NG', // Nigeria
  'ZA', // South Africa
  'TZ', // Tanzania
  'TH', // Thailand
  'VN', // Vietnam
];

// cspell:ignore DLOCAL
export const DLOCAL_STABLECOIN_SUPPORTED_COUNTRIES: string[] = [];

export const OTP_EXPIRATION_MINUTES = 10;

// How long the user has to scan the QR code and submit their first code. Stytch permanently
// kills the registration once this lapses, so a window measured in minutes strands anyone who
// has to switch to another device to scan. Stytch allows 5 to 1440.
export const TOTP_ENROLLMENT_EXPIRATION_MINUTES = 60;

// Stytch answers a passcode the user got wrong with 401, the same status it uses for a dead
// session. Signing someone out mid-verification strands them, so neither the authenticator code
// nor the SMS/WhatsApp/email code may be read as a session expiry. Both channels belong here:
// they are separate error types but the same mistake.
const MFA_REJECTED_CODE_ERROR_TYPES = ['unable_to_authenticate_totp', 'unable_to_auth_otp_code'];

export const isMFARejectedCodeError = (errorType?: string): boolean =>
  MFA_REJECTED_CODE_ERROR_TYPES.includes(errorType ?? '');

export const WORKER_PROFILE_STATUS_EXPLANATION = {
  company_defined_attributes: 'Required employee information has not been submitted by the employer.',
  workplaces: 'The employee does not have a primary workplace.',
  withholdings: 'The employee has not configured withholding parameters in all applicable jurisdictions.',
  employee_details: 'The employee has not completed onboarding.',
};

export const NOTIFICATION_SETTINGS: NotificationSetting[] = [
  {
    id: 'approval-requests',
    title: 'Approval Requests',
    description: 'Receive an email when a contractor submits an invoice.',
    defaultValue: (user, currentCompany) =>
      user?.notificationsByCompanyId?.[currentCompany?.companyID]?.notifyAdminsOnInvoiceSubmission ?? false,
  },
  {
    id: 'approval-requests-pto',
    title: 'Time Off Requests',
    description: 'Receive an email when a contractor submits a time off request.',
    defaultValue: (user, currentCompany) =>
      user?.notificationsByCompanyId?.[currentCompany?.companyID]?.notifyAdminsOnPTOSubmission ?? false,
  },
  {
    id: 'overdue-invoices',
    title: 'Overdue Invoices',
    description: 'Receive an email when there are overdue invoices.',
    disabled: true,
    defaultValue: false,
  },
  {
    id: 'pending-approvals',
    title: 'Pending Approvals',
    description: 'Receive an email when there are pending approvals before an invoice cycle ends.',
    disabled: true,
    defaultValue: false,
  },
  {
    id: 'contract-signature',
    title: 'Contract Signature',
    description: 'Receive an email with a new contract needs signature.',
    disabled: true,
    defaultValue: false,
  },
  {
    id: 'due-invoices',
    title: 'Due Invoices',
    description: 'Receive an email when there are invoices due in the next 5 days.',
    disabled: true,
    defaultValue: false,
  },
  {
    id: 'autopay-reminder',
    title: 'Autopay Reminder',
    description: "Receive an email when there's an autopay upcoming.",
    disabled: true,
    defaultValue: false,
  },
];

export const REPRESENTATIVE_TYPES = [
  { value: 'principal_owner', label: 'Principal Owner' },
  { value: 'authorized_signer', label: 'Authorized Signer' },
  { value: 'coborrower', label: 'Coborrower' },
  { value: 'joint', label: 'Joint' },
  { value: 'guarantor', label: 'Guarantor' },
  { value: 'director', label: 'Director' },
];

export const timeOffPolicyAccrualTypeValues = [
  { id: 'MONTHLY', value: 'MONTHLY', label: 'Monthly' },
  { id: 'ANNUAL', value: 'ANNUAL', label: 'Annual' },
];

// PTO V2 Constants — aligned with backend Fern API enums

export const timeOffAccrualMethodValues = [
  {
    id: 'RECURRING',
    value: 'RECURRING',
    label: 'Accrual based',
    description: 'Workers accumulate their allowance over a period of time',
  },
  {
    id: 'LUMP_SUM',
    value: 'LUMP_SUM',
    label: 'Granted all at once',
    description: 'Workers receive their full allowance at the start of the year',
  },
  {
    id: 'HOURS_WORKED',
    value: 'HOURS_WORKED',
    label: 'Based on hours worked',
    description: 'Workers earn time off for every hour they work, applied when payroll runs',
  },
];

export const timeOffAccrualFrequencyValues = [
  { id: 'DAILY', value: 'DAILY', label: 'Daily' },
  { id: 'WEEKLY', value: 'WEEKLY', label: 'Weekly' },
  { id: 'BIWEEKLY', value: 'BIWEEKLY', label: 'Biweekly' },
  { id: 'SEMI_MONTHLY', value: 'SEMI_MONTHLY', label: 'Semi-monthly' },
  { id: 'MONTHLY', value: 'MONTHLY', label: 'Monthly' },
  { id: 'ANNUALLY', value: 'ANNUALLY', label: 'Annually' },
  { id: 'PER_PAY_CYCLE', value: 'PER_PAY_CYCLE', label: 'Per pay cycle' },
];

export const timeOffCarryoverTypeValues = [
  { id: 'NONE', value: 'NONE', label: 'No Carryover' },
  { id: 'FULL', value: 'FULL', label: 'Full Carryover' },
  { id: 'CAPPED', value: 'CAPPED', label: 'Capped Carryover' },
];

export const timeOffNegativeBalanceBehaviorValues = [
  { id: 'BLOCK', value: 'BLOCK', label: 'Block Request', description: 'Reject the request if it exceeds balance' },
  { id: 'WARN', value: 'WARN', label: 'Allow with Warning', description: 'Allow but notify admin' },
  {
    id: 'APPROVAL_QUEUE',
    value: 'APPROVAL_QUEUE',
    label: 'Require Approval',
    description: 'Route to admin approval queue',
  },
];

export const timeOffAllowanceUnitValues = [
  { id: 'DAYS', value: 'DAYS', label: 'Days' },
  { id: 'HOURS', value: 'HOURS', label: 'Hours' },
];

export const timeOffWaitingPeriodUnitValues = [
  { id: 'DAYS', value: 'DAYS', label: 'Days' },
  { id: 'WEEKS', value: 'WEEKS', label: 'Weeks' },
  { id: 'MONTHS', value: 'MONTHS', label: 'Months' },
];

// Keep old names as aliases for backward compat in existing components
export const timeOffAccrualScheduleValues = timeOffAccrualFrequencyValues;

// Expense/Reimbursement types dropdown options
export const expenseTypesDropdown = [
  { value: 'car_and_mileage', label: 'Car and Mileage', id: 'car_and_mileage' },
  { value: 'employee_benefits', label: 'Employee Benefits', id: 'employee_benefits' },
  { value: 'learning_and_development', label: 'Learning and Development', id: 'learning_and_development' },
  { value: 'meals_and_entertainment', label: 'Meals and Entertainment', id: 'meals_and_entertainment' },
  { value: 'office_expenses_and_supplies', label: 'Office Expenses and Supplies', id: 'office_expenses_and_supplies' },
  { value: 'shipping', label: 'Shipping', id: 'shipping' },
  { value: 'supplies', label: 'Supplies', id: 'supplies' },
  { value: 'travel', label: 'Travel', id: 'travel' },
  { value: 'other', label: 'Other', id: 'other' },
];

// Helper function to get expense type label from value
export const getExpenseTypeLabel = (value: string): string => {
  const expenseType = expenseTypesDropdown.find((type) => type.value === value);
  return expenseType?.label || 'Other';
};
